import { useMemo, useState } from "react";
import {
  removeRolodexEntry,
  saveRolodexEntry,
} from "../lib/rolodexStore";
import {
  descendantNodeIds,
} from "../lib/facetForm";
import { useRelationshipDraft } from "../hooks/useRelationshipEditing";
import { contactProfileForEntry } from "../lib/privateContactProfile";
import FacetEditor from "./FacetEditor";
import PrivateContactFields, {
  EMPTY_PRIVATE_CONTACT_PROFILE,
} from "./PrivateContactFields";

function getInitials(name) {
  return (name || "?")
    .split(" ")
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

const LABELS = { professional: "Professional", personal: "Personal" };

export default function ConnectionEditModal({
  entryId,
  entries = [],
  currentUser,
  onClose,
  networkNodes,
  initialContext = "professional",
}) {
  const entry = entries.find((item) => item.id === entryId) || null;
  const relationship = useRelationshipDraft({
    entry,
    entries,
    initialActiveContext: initialContext,
    sourceKey: entryId,
  });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [recipientName, setRecipientName] = useState(entry?.recipient_name || "");
  const [privateProfile, setPrivateProfile] = useState({
    ...EMPTY_PRIVATE_CONTACT_PROFILE,
    ...(entry?.private_profile || {}),
  });

  const profile = contactProfileForEntry(entry);
  const name = profile.full_name || entry?.recipient_name || entry?.recipient_phone || "Unknown";
  const isPrivateContact = Boolean(entry && !entry.recipient_uid);
  const missingContext = relationship.missingContext;
  const excludedParentIds = useMemo(() => {
    const descendants = descendantNodeIds(entries, entryId, relationship.activeContext);
    descendants.add(entry?.recipient_uid || entry?.id);
    descendants.add(entry?.id);
    return descendants;
  }, [entries, entryId, relationship.activeContext, entry]);

  const addContext = () => {
    if (!missingContext) return;
    relationship.addContext(missingContext);
  };

  const handleSave = async () => {
    if (!entry) return;
    const saved = await relationship.submit(
      () => saveRolodexEntry(currentUser.uid, {
        ...entry,
        ...(isPrivateContact
          ? {
              recipient_name: recipientName.trim(),
              private_profile: privateProfile,
            }
          : {}),
        facets: relationship.storedFacets(entries),
      }),
      { errorMessage: "Changes could not be saved. Try again." },
    );
    if (saved) onClose();
  };

  const handleDelete = async () => {
    if (!entry) return;
    const removed = await relationship.submit(
      () => removeRolodexEntry(currentUser.uid, entry),
      { errorMessage: "The connection could not be removed. Try again." },
    );
    if (removed) onClose();
  };

  return (
    <div className="modal-overlay" onClick={(event) => event.target === event.currentTarget && onClose()}>
      <div className="conn-edit-modal-card context-edit-modal">
        <div className="conn-edit-modal-header">
          <div className="conn-edit-modal-title">
            <div className="detail-avatar context-edit-avatar">
              {profile.profile_photo_url ? (
                <img src={profile.profile_photo_url} alt={name} />
              ) : getInitials(name)}
            </div>
            <div>
              <div className="detail-name context-edit-name">{name}</div>
              <div className="detail-role">Edit each side independently</div>
            </div>
          </div>
          <button className="conn-edit-close-btn" onClick={onClose} aria-label="Close">×</button>
        </div>

        {!entry ? (
          <div className="context-edit-empty">Connection not found.</div>
        ) : (
          <div className="context-edit-body">
            {relationship.submissionError && (
              <div className="error-banner">{relationship.submissionError}</div>
            )}

            {isPrivateContact && (
              <PrivateContactFields
                idPrefix="edit-private-contact"
                name={recipientName}
                onNameChange={setRecipientName}
                value={privateProfile}
                onChange={setPrivateProfile}
                disabled={relationship.isSubmitting}
              />
            )}

            {missingContext && (
              <button className={`add-context-button context-${missingContext}`} onClick={addContext}>
                <span aria-hidden="true">+</span>
                Add {LABELS[missingContext]} side
              </button>
            )}

            <FacetEditor
              relationship={relationship}
              networkNodes={networkNodes}
              currentUid={currentUser?.uid}
              excludeIds={excludedParentIds}
              allowContextRemoval
              canRemoveFacet={relationship.enabledContexts.length > 1}
            />

            {relationship.pendingRemoval && (
              <div className="facet-remove-confirm" role="alertdialog" aria-label={`Remove ${LABELS[relationship.pendingRemoval]}`}>
                <div>
                  <strong>Remove the {LABELS[relationship.pendingRemoval]} side?</strong>
                  <p>Its notes and map origin will be deleted. The other side stays intact.</p>
                </div>
                <div>
                  <button className="btn-ghost btn-sm" onClick={relationship.cancelRemoval}>Keep it</button>
                  <button className="btn-danger-ghost btn-sm" onClick={relationship.confirmRemoval}>Remove side</button>
                </div>
              </div>
            )}

            <div className="conn-edit-modal-footer context-edit-footer">
              <button
                className="btn-primary"
                onClick={handleSave}
                disabled={
                  relationship.isSubmitting ||
                  relationship.pendingRemoval ||
                  (isPrivateContact && !recipientName.trim())
                }
              >
                {relationship.isSubmitting ? "Saving…" : "Save changes"}
              </button>

              {!showDeleteConfirm ? (
                <button
                  className="btn-danger-ghost btn-sm"
                  onClick={() => setShowDeleteConfirm(true)}
                  disabled={relationship.isSubmitting}
                >
                  Remove person entirely
                </button>
              ) : (
                <div className="conn-edit-confirm">
                  <p>Remove {name} from Personal and Professional?</p>
                  <div className="context-delete-actions">
                    <button
                      className="btn-danger-ghost btn-sm"
                      onClick={handleDelete}
                      disabled={relationship.isSubmitting}
                    >
                      Remove person
                    </button>
                    <button
                      className="btn-ghost btn-sm"
                      onClick={() => setShowDeleteConfirm(false)}
                      disabled={relationship.isSubmitting}
                    >
                      Keep person
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
